import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, LogOut, Upload, X, Image } from 'lucide-react';
import logo from '@/assets/bunadstuggu-logo.png';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  image_url: string | null;
  published: boolean;
  created_at: string;
}

const Admin = () => {
  const { user, canManagePosts, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [published, setPublished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (canManagePosts) {
      fetchPosts();
    }
  }, [canManagePosts]);

  const fetchPosts = async () => {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      toast.error('Kunne ikke hente innlegg');
    } else {
      setPosts(data || []);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vennligst velg en bildefil');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bildet må være mindre enn 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(fileName);

      setImageUrl(publicUrl);
      toast.success('Bilde lastet opp!');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Kunne ikke laste opp bildet');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    setImageUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (editingPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update({
            title,
            content,
            excerpt: excerpt || null,
            image_url: imageUrl || null,
            published
          })
          .eq('id', editingPost.id);

        if (error) throw error;
        toast.success('Innlegg oppdatert!');
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert({
            title,
            content,
            excerpt: excerpt || null,
            image_url: imageUrl || null,
            published,
            author_id: user?.id
          });

        if (error) throw error;
        toast.success('Innlegg opprettet!');
      }

      resetForm();
      fetchPosts();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.content);
    setExcerpt(post.excerpt || '');
    setImageUrl(post.image_url || '');
    setPublished(post.published);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette dette innlegget?')) return;

    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (error) {
      toast.error('Kunne ikke slette innlegget');
    } else {
      toast.success('Innlegg slettet');
      fetchPosts();
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingPost(null);
    setTitle('');
    setContent('');
    setExcerpt('');
    setImageUrl('');
    setPublished(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p>Laster...</p>
      </div>
    );
  }

  if (!canManagePosts) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <img src={logo} alt="Bunadstuggu" className="h-16 mx-auto mb-6" />
          <h1 className="text-xl font-serif text-charcoal mb-4">Ingen tilgang</h1>
          <p className="text-charcoal/70 mb-6">
            Du har ikke administratortilgang. Kontakt eieren av siden for å få tilgang.
          </p>
          <div className="space-y-2">
            <Button onClick={handleSignOut} variant="outline" className="w-full">
              <LogOut className="w-4 h-4 mr-2" />
              Logg ut
            </Button>
            <a href="/" className="block text-coral hover:underline text-sm">
              ← Tilbake til forsiden
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={logo} alt="Bunadstuggu" className="h-12" />
          <div className="flex items-center gap-4">
            <span className="text-sm text-charcoal/70">{user?.email}</span>
            <Button onClick={handleSignOut} variant="outline" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              Logg ut
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif text-charcoal">Administrer Aktuelt</h1>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nytt innlegg
            </Button>
          )}
        </div>

        {isEditing ? (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-serif text-charcoal mb-6">
              {editingPost ? 'Rediger innlegg' : 'Nytt innlegg'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Tittel *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="excerpt">Kort beskrivelse</Label>
                <Input
                  id="excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Vises i forhåndsvisningen"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="content">Innhold *</Label>
                <Textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  rows={8}
                  className="mt-1"
                />
              </div>

              {/* Image Upload Section */}
              <div>
                <Label>Bilde</Label>
                <div className="mt-2">
                  {imageUrl ? (
                    <div className="relative inline-block">
                      <img 
                        src={imageUrl} 
                        alt="Forhåndsvisning" 
                        className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label 
                      className={`
                        flex flex-col items-center justify-center w-full max-w-md h-48 
                        border-2 border-dashed border-gray-300 rounded-lg cursor-pointer
                        hover:border-coral hover:bg-coral/5 transition-all duration-200
                        ${isUploading ? 'opacity-50 cursor-wait' : ''}
                      `}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center text-charcoal/60">
                        {isUploading ? (
                          <>
                            <div className="w-8 h-8 border-2 border-coral border-t-transparent rounded-full animate-spin mb-2" />
                            <span className="text-sm">Laster opp...</span>
                          </>
                        ) : (
                          <>
                            <Image className="w-10 h-10 mb-2 text-charcoal/40" />
                            <span className="text-sm font-medium">Klikk for å laste opp bilde</span>
                            <span className="text-xs mt-1">JPG, PNG eller WebP (maks 5MB)</span>
                          </>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 py-2">
                <Switch
                  id="published"
                  checked={published}
                  onCheckedChange={setPublished}
                />
                <Label htmlFor="published" className="cursor-pointer">
                  Publiser innlegget
                </Label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Lagrer...' : editingPost ? 'Oppdater' : 'Opprett'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Avbryt
                </Button>
              </div>
            </form>
          </div>
        ) : null}

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-serif text-charcoal">Alle innlegg</h2>
          </div>
          
          {posts.length === 0 ? (
            <div className="p-8 text-center text-charcoal/60">
              Ingen innlegg ennå. Opprett ditt første innlegg!
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {posts.map((post) => (
                <div key={post.id} className="p-4 flex items-center gap-4">
                  {post.image_url && (
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-charcoal truncate">{post.title}</h3>
                    <p className="text-sm text-charcoal/60">
                      {new Date(post.created_at).toLocaleDateString('nb-NO')}
                      {' • '}
                      <span className={post.published ? 'text-green-600' : 'text-amber-600'}>
                        {post.published ? 'Publisert' : 'Utkast'}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(post)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(post.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-coral hover:underline">
            ← Tilbake til forsiden
          </a>
        </div>
      </main>
    </div>
  );
};

export default Admin;
